import type { BuildingId, SpaceId } from '@store/models/ids';
import type {
  CreateEventRequest,
  PatchEventRequest,
  SubmitEventRequest,
  Sponsor,
  OnSiteContact,
} from '@store/models';
import { getActiveAccount } from '@/services';
import { convertBuildingLocalDateTimeToUtcIso } from './timeZone';
import { buildRequestId } from './requestIdBuilder';

function extractNested(data: Record<string, unknown>, prefix: string): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (key.startsWith(`${prefix}.`)) {
      const nested = key.slice(prefix.length + 1);
      result[nested] = value;
    }
  }
  return result;
}

/**
 * Combines a date string and optional time string ("09:00")
 * into an ISO datetime string ("2026-04-15T09:00:00Z").
 * Handles both date-only ("2026-04-15") and full ISO ("2026-04-15T00:00:00.000Z") inputs.
 * Falls back to midnight if no time is provided.
 */
function combineDateAndTime(date: string, time?: string, timeZone?: string): string {
  if (!date) return '';
  const dateOnly = date.slice(0, 10);
  return convertBuildingLocalDateTimeToUtcIso(dateOnly, time, timeZone);
}

export function mapFormDataToCreateRequest(
  formData: Record<string, unknown>,
  options?: { buildingTimeZone?: string }
): CreateEventRequest {
  const dateRange = formData.eventDateRange as { start?: string; end?: string } | undefined;
  const timeRange = formData.eventTimeRange as { start?: string; end?: string } | undefined;
  const sponsorData = extractNested(formData, 'sponsor');

  const result: CreateEventRequest = {
    // Lifecycle stability (FR-008) is enforced by the caller — see
    // useBookingForm.ts dispatch sites — not by this generator. This
    // builder is only invoked on the initial-create path; rename / save /
    // patch / submit / cancel paths reuse the id from the store via
    // setRequestId, never regenerate one.
    requestId: buildRequestId(),
    buildingId: formData.buildingId as BuildingId,
  };

  const startDate = combineDateAndTime(
    dateRange?.start ?? '',
    timeRange?.start,
    options?.buildingTimeZone
  );
  const endDate = combineDateAndTime(
    dateRange?.end ?? dateRange?.start ?? '',
    timeRange?.end,
    options?.buildingTimeZone
  );
  if (startDate) result.eventStartDate = startDate;
  if (endDate) result.eventEndDate = endDate;
  if (formData.numberOfAttendees != null && (formData.numberOfAttendees as number) > 0) {
    result.numberOfAttendees = formData.numberOfAttendees as number;
  }

  if (formData.buildingName) result.buildingName = formData.buildingName as string;
  if (formData.currentStep) result.currentStep = formData.currentStep as string;

  if (formData.eventTitle) result.eventTitle = formData.eventTitle as string;
  if (formData.selectedSpace) {
    const selected = formData.selectedSpace as { id: string; name?: string };
    const space: NonNullable<CreateEventRequest['spaces']>[number] = {
      spaceId: selected.id as unknown as SpaceId,
    };
    if (selected.name) space.spaceName = selected.name;
    if (formData.selectedSetups) {
      space.selectedSetupCodes = formData.selectedSetups as string[];
    }
    result.spaces = [space];
  }
  if (formData.visitPurposeCode) result.visitPurposeCode = formData.visitPurposeCode as string;
  if (formData.businessGroupCode) result.businessGroupCode = formData.businessGroupCode as string;
  if (formData.businessJustification)
    result.businessJustification = formData.businessJustification as string;
  if (formData.attendeeTypeCode) result.attendeeTypeCode = formData.attendeeTypeCode as string;
  if (Object.keys(sponsorData).length > 0) {
    result.sponsor = sponsorData as Partial<Sponsor>;
  }
  result.hasOnSiteContact = formData.hasOnSiteContact === 'yes';
  if (formData.hasOnSiteContact === 'yes' && formData.onSiteContacts) {
    result.onSiteContacts = Array.isArray(formData.onSiteContacts)
      ? (formData.onSiteContacts as OnSiteContact[])
      : [{ name: formData.onSiteContacts as string, email: '' }];
  }
  if (formData.additionalInformation)
    result.additionalInformation = formData.additionalInformation as string;

  const account = getActiveAccount();
  if (account?.username) result.submittedByEmail = account.username;
  if (account?.name) result.submittedByName = account.name;

  return result;
}

export function mapFormDataToPatchRequest(
  formData: Record<string, unknown>,
  options?: { buildingTimeZone?: string }
): PatchEventRequest {
  const dateRange = formData.eventDateRange as { start?: string; end?: string } | undefined;
  const timeRange = formData.eventTimeRange as { start?: string; end?: string } | undefined;
  const sponsorData = extractNested(formData, 'sponsor');

  const patch: PatchEventRequest = {};

  // Location/DateTime fields
  if (dateRange?.start)
    patch.eventStartDate = combineDateAndTime(
      dateRange.start,
      timeRange?.start,
      options?.buildingTimeZone
    );
  if (dateRange?.end)
    patch.eventEndDate = combineDateAndTime(
      dateRange.end,
      timeRange?.end,
      options?.buildingTimeZone
    );
  if (formData.numberOfAttendees !== undefined)
    patch.numberOfAttendees = formData.numberOfAttendees as number;
  if (formData.eventTitle !== undefined) patch.eventTitle = formData.eventTitle as string;

  // Canonical `spaces` array: building identity travels INSIDE each space entry on a PATCH
  // body (singular `space` is unsupported per API contract). Top-level `buildingId`/`buildingName`
  // are not valid on PatchEventRequest. Whenever the building OR space changed, send a spaces entry
  // with building identity populated; space-identity fields are populated when a space is selected.
  if (formData.buildingId || formData.selectedSpace) {
    const selectedSpace = formData.selectedSpace as
      | { id: string; name?: string; buildingId?: BuildingId }
      | undefined;
    const currentBuildingId =
      (formData.buildingId as BuildingId) ??
      (formData.currentBuildingId as BuildingId) ??
      selectedSpace?.buildingId;

    const space: NonNullable<PatchEventRequest['spaces']>[number] = {};
    if (currentBuildingId) {
      space.buildingId = currentBuildingId;
    }
    if (formData.buildingName !== undefined) {
      space.buildingName = formData.buildingName as string;
    }
    if (selectedSpace) {
      space.spaceId = selectedSpace.id as unknown as SpaceId;
      if (selectedSpace.name !== undefined) space.spaceName = selectedSpace.name;
      if (formData.selectedSetups !== undefined) {
        space.selectedSetupCodes = formData.selectedSetups as string[];
      }
    }
    patch.spaces = [space];
  }

  // Event details
  if (formData.visitPurposeCode !== undefined)
    patch.visitPurposeCode = formData.visitPurposeCode as string;
  if (formData.businessGroupCode !== undefined)
    patch.businessGroupCode = formData.businessGroupCode as string;
  if (formData.businessJustification !== undefined)
    patch.businessJustification = formData.businessJustification as string;
  if (formData.attendeeTypeCode !== undefined)
    patch.attendeeTypeCode = formData.attendeeTypeCode as string;

  // Sponsor
  if (Object.keys(sponsorData).length > 0) {
    patch.sponsor = sponsorData as Partial<Sponsor>;
  }

  // On-site contacts
  if (formData.hasOnSiteContact !== undefined)
    patch.hasOnSiteContact = formData.hasOnSiteContact === 'yes';
  if (formData.onSiteContacts) {
    patch.onSiteContacts = Array.isArray(formData.onSiteContacts)
      ? (formData.onSiteContacts as OnSiteContact[])
      : [{ name: formData.onSiteContacts as string, email: '' }];
  }

  // Additional details
  if (formData.additionalInformation !== undefined)
    patch.additionalInformation = formData.additionalInformation as string;
  if (formData.hasCatering !== undefined) patch.hasCatering = formData.hasCatering === 'yes';

  // Additional requirements
  if (formData.filmingRequired !== undefined)
    patch.filmingRequired = formData.filmingRequired === 'yes';
  if (formData.overnightStorageRequired !== undefined)
    patch.overnightStorageRequired = formData.overnightStorageRequired === 'yes';
  if (formData.shipmentsExpected !== undefined)
    patch.shipmentsExpected = formData.shipmentsExpected === 'yes';

  return patch;
}

export function mapFormDataToSubmitRequest(formData: Record<string, unknown>): SubmitEventRequest {
  const sponsorData = extractNested(formData, 'sponsor');

  const result: SubmitEventRequest = {
    eventTitle: (formData.eventTitle as string) ?? '',
    visitPurposeCode: (formData.visitPurposeCode as string) ?? '',
    businessGroupCode: formData.businessGroupCode as string | undefined,
    businessJustification: formData.businessJustification as string | undefined,
    attendeeTypeCode: (formData.attendeeTypeCode as string) ?? '',
    sponsor: {
      name: (sponsorData.name as string) ?? '',
      email: (sponsorData.email as string) ?? '',
      costCenter: (sponsorData.costCenter as string) ?? '',
    },
    hasOnSiteContact: formData.hasOnSiteContact === 'yes',
    additionalInformation: (formData.additionalInformation as string) ?? '',
    acknowledgement: {
      policiesAccepted: !!formData.policyAcknowledgement,
      acceptedAt: new Date().toISOString(),
    },
  };

  if (formData.hasOnSiteContact === 'yes' && formData.onSiteContacts) {
    result.onSiteContacts = Array.isArray(formData.onSiteContacts)
      ? (formData.onSiteContacts as OnSiteContact[])
      : [{ name: formData.onSiteContacts as string, email: '' }];
  }

  return result;
}
